import {
  usePortalOvertimeAcknowledgeMutation,
  usePortalOvertimeQuery,
  usePortalOvertimeRequestCreateMutation,
} from "@dashboard/graphql";
import { Box, Button, Input, Text } from "@saleor/macaw-ui-next";
import { useState } from "react";
import { useIntl } from "react-intl";

import { ErrorText, InlineText, Page, Section } from "../components";
import { portalMessages as m } from "../messages";
import { OvertimeCountersView } from "../OvertimeParts";
import { formatDateTime, formatDay, formatMinutes, today } from "../utils";

export const Overtime = () => {
  const intl = useIntl();
  const { data, refetch } = usePortalOvertimeQuery({ fetchPolicy: "cache-and-network" });
  const [form, setForm] = useState({ date: today(), minutes: "60", reason: "" });
  const [create, createOpts] = usePortalOvertimeRequestCreateMutation({
    onCompleted: result => {
      if (!result.overtimeRequestCreate?.errors.length) {
        setForm({ date: today(), minutes: "60", reason: "" });
        refetch();
      }
    },
  });

  const [declining, setDeclining] = useState<string | null>(null);
  const [acknowledge, ackOpts] = usePortalOvertimeAcknowledgeMutation({
    onCompleted: result => {
      if (!result.overtimeAcknowledge?.errors.length) {
        setDeclining(null);
        refetch();
      }
    },
  });

  return (
    <Page>
      {data?.myOvertimeCounters && (
        <Section>
          <OvertimeCountersView counters={data.myOvertimeCounters} />
        </Section>
      )}
      <Section title={intl.formatMessage(m.overtimeTitle)}>
        <Box display="grid" gap={2} __gridTemplateColumns="repeat(auto-fit, minmax(160px, 1fr))">
          <Input
            size="small"
            type="date"
            label={intl.formatMessage(m.date)}
            value={form.date}
            onChange={event => setForm({ ...form, date: event.target.value })}
          />
          <Input
            size="small"
            type="number"
            min={15}
            max={720}
            step={15}
            label={intl.formatMessage(m.minutes)}
            value={form.minutes}
            onChange={event => setForm({ ...form, minutes: event.target.value })}
          />
          <Input
            size="small"
            label={intl.formatMessage(m.reason)}
            value={form.reason}
            maxLength={2000}
            onChange={event => setForm({ ...form, reason: event.target.value })}
          />
        </Box>
        <Box>
          <Button
            size="small"
            variant="primary"
            disabled={!form.reason.trim() || !Number(form.minutes) || createOpts.loading}
            onClick={() =>
              create({
                variables: {
                  date: form.date,
                  minutes: Number(form.minutes),
                  reason: form.reason.trim(),
                },
              })
            }
          >
            {intl.formatMessage(m.requestOvertime)}
          </Button>
        </Box>
        <ErrorText errors={createOpts.data?.overtimeRequestCreate?.errors} />
        {(data?.myOvertimeRequests ?? []).map(request => (
          <Box key={request.id} display="flex" gap={2} flexWrap="wrap" alignItems="baseline">
            <Text size={3} fontWeight="bold">
              {formatDay(request.date)}
            </Text>
            <Text size={2}>
              {formatMinutes(request.minutes)} · {request.reason} ·{" "}
              {intl.formatMessage(m.by, { name: request.requestedByName })}
            </Text>
            <Text
              size={2}
              color={
                request.approved === null ? "default2" : request.approved ? "success1" : "critical1"
              }
            >
              {intl.formatMessage(
                request.approved === null ? m.pending : request.approved ? m.approved : m.refused,
              )}
              {request.decisionNote ? ` — ${request.decisionNote}` : ""}
              {request.legalReasonLabel ? ` · ${request.legalReasonLabel}` : ""}
            </Text>
            {request.ordered && request.approved && (
              <>
                {request.acknowledged === null ? (
                  declining === request.id ? (
                    <InlineText
                      placeholder={intl.formatMessage(m.declineReason)}
                      loading={ackOpts.loading}
                      onCancel={() => setDeclining(null)}
                      onSubmit={note =>
                        acknowledge({ variables: { id: request.id, accepted: false, note } })
                      }
                    />
                  ) : (
                    <>
                      <Button
                        size="small"
                        variant="primary"
                        disabled={ackOpts.loading}
                        onClick={() =>
                          acknowledge({ variables: { id: request.id, accepted: true } })
                        }
                      >
                        {intl.formatMessage(m.takeNote)}
                      </Button>
                      <Button
                        size="small"
                        variant="secondary"
                        onClick={() => setDeclining(request.id)}
                      >
                        {intl.formatMessage(m.decline)}
                      </Button>
                    </>
                  )
                ) : (
                  <Text size={2} color={request.acknowledged ? "success1" : "critical1"}>
                    {intl.formatMessage(request.acknowledged ? m.acknowledged : m.declined)}
                    {request.acknowledgementNote ? ` — ${request.acknowledgementNote}` : ""}
                  </Text>
                )}
              </>
            )}
          </Box>
        ))}
        <ErrorText errors={ackOpts.data?.overtimeAcknowledge?.errors} />
      </Section>
      {(data?.myStayOppositions.length ?? 0) > 0 && (
        <Section title={intl.formatMessage(m.oppositions)}>
          {data?.myStayOppositions.map(item => (
            <Text key={item.id} size={2}>
              {formatDay(item.date)} — “{item.note}” · {item.recordedByName} ·{" "}
              {formatDateTime(item.recordedAt)}
            </Text>
          ))}
        </Section>
      )}
    </Page>
  );
};
